import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QrCode, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Account,
  AccountContactsMetadata,
  PrivacyMode,
} from "@/types/generated";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "@/services/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";

interface AccountCardProps {
  account: Account;
  onLogin: (id: string) => void;
  onLogout: (id: string) => void;
  onSync: (id: string) => void;
  onPrivacyModeChange: (id: string, mode: PrivacyMode) => void;
}

export const AccountCard = ({
  account,
  onLogin,
  onLogout,
  onSync,
  onPrivacyModeChange,
}: AccountCardProps) => {
  const { t } = useTranslation();
  const { data: metadata } = useQuery<AccountContactsMetadata>({
    queryKey: ["contacts", "metadata", account.id],
    queryFn: async () => {
      const response = await apiService.getAccountContactsMetadata(account.id);
      return response.data;
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-xl font-medium">
            {account.name || t("accounts.default_name")}
          </CardTitle>
          {metadata && (
            <Badge variant="secondary" className="ml-2">
              {metadata.total} 👥
            </Badge>
          )}
        </div>
        <div
          className={`px-2 py-1 rounded-full text-xs ${
            account.status === "connected"
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {t(`accounts.status.${account.status}`)}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onSync(account.id)}
            className="h-8 w-8"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mt-4 space-y-4">
          {account.status === "connected" && (
            <Select
              value={account.settings?.privacy_mode || "default"}
              onValueChange={(value) =>
                onPrivacyModeChange(account.id, value as PrivacyMode)
              }
            >
              <SelectTrigger>
                <SelectValue translate="no" placeholder={t("accounts.select_privacy_mode")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t("accounts.privacy.default")}</SelectItem>
                <SelectItem value="chats">{t("accounts.privacy.chats")}</SelectItem>
                <SelectItem value="contacts">{t("accounts.privacy.contacts")}</SelectItem>
                {/*<SelectItem value="whitelist">רשימה לבנה</SelectItem>
                <SelectItem value="blacklist">רשימה שחורה</SelectItem>
                <SelectItem value="custom">מותאם אישית</SelectItem> */}
              </SelectContent>
            </Select>
          )}
          {account.status === "disconnected" ? (
            <Button className="w-full" onClick={() => onLogin(account.id)}>
              <QrCode className="mr-2 h-4 w-4" />
              {t("accounts.connect_whatsapp")}
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="w-full" variant="outline">
                  {account.status === "creating" ? (
                    <Trash2 className="mr-2 h-4 w-4" />
                  ) : (
                    <LogOut className="mr-2 h-4 w-4" />
                  )}
                  {t("accounts.disconnect")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("common.are_you_sure")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("accounts.disconnect_confirmation")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onLogout(account.id)}>
                    {t("accounts.disconnect")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
